import {useEffect, useRef, type RefObject} from "react";
import {useLocation} from "react-router-dom";
import {requestComposerTheme} from "home/personalization/composerThemeRequest";
import {bringComposerIntoView} from "./composerChooser";
import {editionTemplate, halloweenWindow, requestedHalloweenEdition, type HalloweenSwitch} from "./halloweenEdition";

/**
 * A visitor the recipient page or the guests' mail sent to the landing for the Halloween slide (`?tema=pumpkin`,
 * kitecorp/transferify-ui#3776) came for the composer with that slide's scene on: put the scene on its draft, as
 * the slide's own press does, and bring them to it, each once per visit. The address stays as it came: a
 * replace-navigation that dropped the query re-rendered the composer's controls and took the focus with it
 * (measured 2026-10-26: the focused button left the document 80 ms after focus landed on it). Which arrival counts
 * is `requestedHalloweenEdition`'s rule; this hook only supplies the address, the clock, the api's switch as read
 * so far (`halloween`, which can turn the arrival on after the constant days said no) and the page.
 */
export function useHalloweenArrival(active: boolean, surface: RefObject<HTMLElement | null>, halloween: HalloweenSwitch | null | undefined): void {
	const {search} = useLocation();
	const themed = useRef(false);
	const brought = useRef(false);
	useEffect(() => {
		if (brought.current || !active) return;
		const edition = requestedHalloweenEdition(search, halloweenWindow(Date.now(), halloween ?? null));
		if (edition === null) return;
		if (!themed.current) {
			themed.current = true;
			requestComposerTheme(editionTemplate(edition));
		}
		brought.current = bringComposerIntoView(surface.current);
	}, [active, search, surface, halloween]);
}
